import React from 'react';
import { addNewActivity } from '../../utils/activityLogger';
import CustomDropdown from "../reusableComponents/CustomDropdown";
import GardenManagerPastEventsPopup from "./GardenManagerPastEventsPopup";
import '../../css/gardenManagerStylings/GardenManagerEventPopupStyling.css';

// moved to outside component to prevent recreation on every render
const EVENT_TYPES = [
    "watered", "fertilized", "status_changed", "planted", "harvested", "treated_for_pests",
    "soil_tested", "transplanted", "weeded", "mulched", "pruned", "support_added", "soil_tilled",
    "frost_protection_added", "frost_protection_removed"
].map(event => ({
    value: event,
    label: event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}));

// Helper functions moved outside component
const getCurrentDate = () => new Date().toISOString().split('T')[0];
const getCurrentTime = () => new Date().toTimeString().slice(0, 5);

const GardenManagerEventPopup = ({ selectedGarden, gardens, onAddEvent, onClose }) => {
    const [isAddingEvent, setIsAddingEvent] = React.useState(true);
    const [selectedEvent, setSelectedEvent] = React.useState("");
    const [selectedPlantRelation, setSelectedPlantRelation] = React.useState("");
    const [isSavingEvent, setIsSavingEvent] = React.useState(false);

    // Initialize with current date/time only once
    const [eventDate, setEventDate] = React.useState(() => getCurrentDate());
    const [eventTime, setEventTime] = React.useState(() => getCurrentTime());
    const [eventNotes, setEventNotes] = React.useState("");
    const [eventValue, setEventValue] = React.useState("");
    const [eventUnit, setEventUnit] = React.useState("");

    const [statusMessage, setStatusMessage] = React.useState("");
    const [statusType, setStatusType] = React.useState(""); // "success" or "error"

    // Memoize plant options to prevent unnecessary recalculations
    const plantOptions = React.useMemo(() => {
        if (!selectedGarden?.plants) return [];

        console.log(selectedGarden);
        return selectedGarden.plants.map(plant => ({
            value: plant.id,
            label: plant.plant.plant_name
        }));
    }, [selectedGarden?.plants]);

    const handleAddEventTabClick = React.useCallback(() => {
        setIsAddingEvent(true);
    }, []);

    const handleViewEventHistoryTabClick = React.useCallback(() => {
        setIsAddingEvent(false);
    }, []);

    // Memoize the submit handler to prevent recreation
    const handleSubmitEvent = React.useCallback(async () => {
        setIsSavingEvent(true);
        setStatusMessage("");
        setStatusType("");

        const eventData = {
            event_type: selectedEvent,
            plant_id: selectedPlantRelation,
            date: eventDate,
            time: eventTime,
            notes: eventNotes,
            created_at: `${eventDate}T${eventTime}:00`
        };

        console.log('Submitting event:', eventData);

        const subject = selectedPlantRelation ? 'plant' : 'garden';
        const subject_id = selectedPlantRelation || selectedGarden.id;

        // Create formatted datetime string for notes
        const eventDateTime = `${eventDate}T${eventTime}:00`;

        // Build comprehensive notes
        let comprehensiveNotes = `Event occurred on ${eventDate} at ${eventTime}`;
        if (eventNotes.trim()) {
            comprehensiveNotes += `. ${eventNotes}`;
        }

        try {
            // Add activity to database with user's selected date/time in notes
            await addNewActivity(
                'user',                    // actor
                subject,                   // subject ('plant' or 'garden')
                subject_id,                // subject_id (plant ID or garden ID)
                selectedEvent,             // action (the event type)
                eventValue || null,                      // value
                eventUnit || null,                      // unit
                null,                      // old_status
                null,                      // new_status
                comprehensiveNotes         // notes (includes user's date/time + notes)
            );

            // Reset the form
            setSelectedEvent("");
            setSelectedPlantRelation("");
            setEventDate(getCurrentDate());
            setEventTime(getCurrentTime());
            setEventNotes("");
            setEventValue("");
            setEventUnit("");

            // Set success message
            setStatusMessage("Event created successfully!");
            setStatusType("success");

            // Clear the message after 5 seconds
            setTimeout(() => {
                setStatusMessage("");
                setStatusType("");
            }, 5000);

            onAddEvent?.();

            // Switch to view events tab after successful submission
            // setIsAddingEvent(false);

        } catch (error) {
            console.error('Error submitting event:', error);

            // Set error message
            setStatusMessage(error.message || "Failed to create reminder. Please try again.");
            setStatusType("error");

            // Clear the message after 5 seconds
            setTimeout(() => {
                setStatusMessage("");
                setStatusType("");
            }, 5000);
        }

        setIsSavingEvent(false);

    }, [selectedEvent, selectedPlantRelation, eventDate, eventTime, eventNotes]);

    // Close modal when clicking on overlay background
    const handleOverlayClick = React.useCallback((e) => {
        if (e.target === e.currentTarget) {
            onClose?.();
        }
    }, [onClose]);

    // Memoize the close handler
    const handleCloseClick = React.useCallback(() => {
        onClose?.();
    }, [onClose]);

    // Close modal with Escape key
    React.useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Memoize event handlers for form fields
    const handleEventChange = React.useCallback((event) => {
        setSelectedEvent(event.value);
    }, []);

    const handlePlantChange = React.useCallback((relation) => {
        setSelectedPlantRelation(relation.value);
    }, []);

    const handleValueChange = React.useCallback((e) => {
        setEventValue(e.target.value);
    }, []);

    const handleUnitChange = React.useCallback((e) => {
        setEventUnit(e.target.value);
    }, []);

    const handleDateChange = React.useCallback((e) => {
        setEventDate(e.target.value);
    }, []);

    const handleTimeChange = React.useCallback((e) => {
        setEventTime(e.target.value);
    }, []);

    const handleNotesChange = React.useCallback((e) => {
        setEventNotes(e.target.value);
    }, []);

    return (
        <div className={`overlay-background`} onClick={handleOverlayClick}>
            <div className={`event-popup-screen`}>
                <div className={`event-popup-content`}>
                    <div className={`event-popup-header`}>
                        <div className={`event-popup-header-title`}>
                            Garden Events
                        </div>
                        <div className={`event-popup-header-close-button`} onClick={handleCloseClick}>
                            X
                        </div>
                    </div>

                    <div className={`event-popup-page-selector`}>
                        <div
                            className={`add-new-event-button ${isAddingEvent ? 'active' : ''}`}
                            onClick={handleAddEventTabClick}
                        >
                            Add New Event
                        </div>
                        <div
                            className={`view-past-events-button ${!isAddingEvent ? 'active' : ''}`}
                            onClick={handleViewEventHistoryTabClick}
                        >
                            View Past Events
                        </div>
                    </div>

                    {isAddingEvent ? (
                        <div className={`event-popup-main-content`}>
                            <div className={`add-new-event-content`}>

                                <div className={`add-event-field`}>
                                    <label className="add-new-event-field-title">
                                        Event Type *
                                    </label>
                                    <CustomDropdown
                                        options={EVENT_TYPES}
                                        value={selectedEvent}
                                        onChange={handleEventChange}
                                        placeholder="Select an event type..."
                                        className="garden-dropdown-custom"
                                    />
                                </div>

                                <div className={`add-event-field`}>
                                    <label className="add-new-event-field-title">
                                        Plant Relation
                                    </label>
                                    <CustomDropdown
                                        options={plantOptions}
                                        value={selectedPlantRelation}
                                        onChange={handlePlantChange}
                                        placeholder="Select a plant relation if appropriate..."
                                        className="garden-dropdown-custom"
                                    />
                                </div>


                                {/* Value and Unit fields */}
                                <div className={`add-event-value-unit-fields`}>
                                    <div className={`add-event-field add-event-value-field`}>
                                        <label className="add-new-event-field-title">
                                            {selectedEvent ? `${selectedEvent} value` : 'Value'}
                                        </label>
                                        <input
                                            type="text"
                                            value={eventValue}
                                            onChange={handleValueChange}
                                            placeholder="Enter value..."
                                            className="event-text-input"
                                        />
                                    </div>

                                    <div className={`add-event-field add-event-unit-field`}>
                                        <label className="add-new-event-field-title">
                                            {selectedEvent ? `${selectedEvent} unit` : 'Unit'}
                                        </label>
                                        <input
                                            type="text"
                                            value={eventUnit}
                                            onChange={handleUnitChange}
                                            placeholder="Enter unit..."
                                            className="event-text-input"
                                        />
                                    </div>
                                </div>


                                {/* Date and Time Fields */}
                                <div className={`add-event-datetime-container`}>
                                    <div className={`add-event-field add-event-date-field`}>
                                        <label className="add-new-event-field-title">
                                            Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={eventDate}
                                            onChange={handleDateChange}
                                            className="event-date-input"
                                            required
                                        />
                                    </div>

                                    <div className={`add-event-field add-event-time-field`}>
                                        <label className="add-new-event-field-title">
                                            Time *
                                        </label>
                                        <input
                                            type="time"
                                            value={eventTime}
                                            onChange={handleTimeChange}
                                            className="event-time-input"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Notes Field */}
                                <div className={`add-event-field`}>
                                    <label className="add-new-event-field-title">
                                        Notes
                                    </label>
                                    <textarea
                                        value={eventNotes}
                                        onChange={handleNotesChange}
                                        placeholder="Add any additional details about this event..."
                                        className="event-notes-textarea"
                                        rows={4}
                                    />
                                </div>

                                {/* Submit Button */}
                                <div className={`add-event-submit-container`}>
                                    <div className="status-message-container">
                                        {statusMessage && (
                                            <div className={`status-message status-message-${statusType}`}>
                                                {statusMessage}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleSubmitEvent}
                                        className={`add-event-submit-button ${isSavingEvent ? 'disabled' : ''}`}
                                        disabled={!selectedEvent || isSavingEvent}
                                    >
                                        Create Event
                                    </button>
                                </div>

                            </div>
                        </div>
                    ) : (
                        <div className={`event-popup-main-content`}>
                            <div className={`event-history-content`}>
                                {/* Event history content will go here */}

                                <GardenManagerPastEventsPopup selectedGarden={selectedGarden}/>

                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

export default React.memo(GardenManagerEventPopup);